import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../store/authStore";
import { tocarSom, TipoSom } from "../utils/sons";

const KEY_SOM = "growsoft_chat_som";
const KEY_INPAGE = "growsoft_chat_inpage_notif";
const KEY_AUDIO = "growsoft_chat_audio_ativo";
const KEY_VOLUME = "growsoft_chat_som_volume";
const KEY_TIPO = "growsoft_chat_som_tipo";
const KEY_ABRIR_DIRETO = "growsoft_chat_abrir_direto";
const KEY_EMOJI = "growsoft_chat_emoji_picker";

export function getSomAtivo() {
  return localStorage.getItem(KEY_SOM) !== "0";
}
export function setSomAtivo(v: boolean) {
  localStorage.setItem(KEY_SOM, v ? "1" : "0");
}
export function getInPageNotifAtivo() {
  return localStorage.getItem(KEY_INPAGE) !== "0";
}
export function setInPageNotifAtivo(v: boolean) {
  localStorage.setItem(KEY_INPAGE, v ? "1" : "0");
}
export function getAudioGravacaoAtivo() {
  return localStorage.getItem(KEY_AUDIO) !== "0";
}
export function setAudioGravacaoAtivo(v: boolean) {
  localStorage.setItem(KEY_AUDIO, v ? "1" : "0");
  try { window.dispatchEvent(new CustomEvent("growsoft:chat-audio-toggle", { detail: v })); } catch {}
}

// Volume do ding (0..1). Default 0.8 — bem audível.
export function getSomVolume(): number {
  const raw = localStorage.getItem(KEY_VOLUME);
  if (raw == null) return 0.8;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0.8;
  return Math.max(0, Math.min(1, n));
}
export function setSomVolume(v: number) {
  const clamped = Math.max(0, Math.min(1, v));
  localStorage.setItem(KEY_VOLUME, String(clamped));
}

/**
 * "Abrir direto" — quando o user clica no botão flutuante e há mensagens não
 * lidas, abre direto a conversa que chamou primeiro. Quando desligado, sempre
 * abre a lista. Default: ligado (comportamento histórico).
 */
export function getAbrirDiretoAtivo() {
  return localStorage.getItem(KEY_ABRIR_DIRETO) !== "0";
}
export function setAbrirDiretoAtivo(v: boolean) {
  localStorage.setItem(KEY_ABRIR_DIRETO, v ? "1" : "0");
}

/**
 * Picker de emoji no composer do chat. Default: DESLIGADO — usuário ativa
 * via menu de configurações se quiser. Dispara evento pra o ChatThread
 * reagir em tempo real (mostra/esconde o botão sem precisar reabrir).
 */
export function getEmojiPickerAtivo() {
  return localStorage.getItem(KEY_EMOJI) === "1";
}
export function setEmojiPickerAtivo(v: boolean) {
  localStorage.setItem(KEY_EMOJI, v ? "1" : "0");
  try { window.dispatchEvent(new CustomEvent("growsoft:chat-emoji-toggle", { detail: v })); } catch {}
}

// Tipo de som. Default "ding".
const TIPOS_VALIDOS: TipoSom[] = ["ding", "pop", "chime", "blip", "soft"];
export function getSomTipo(): TipoSom {
  const raw = localStorage.getItem(KEY_TIPO) as TipoSom | null;
  if (raw && TIPOS_VALIDOS.includes(raw)) return raw;
  return "ding";
}
export function setSomTipo(t: TipoSom) {
  localStorage.setItem(KEY_TIPO, t);
}

/**
 * Conecta no SSE através de janela e dispara som + Notification quando uma
 * mensagem nova chega numa conversa que NÃO está aberta. O hook useEventStream
 * (já wirado no Layout) cuida da invalidação das queries — aqui só os efeitos
 * "humanos".
 */
export function useChatNotifications() {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  useEffect(() => {
    if (!userId || typeof window === "undefined") return;

    function onMensagemEvent(e: any) {
      const detail = e.detail as { conversaId: string; autorId: string | null; autorIa: boolean };
      if (!detail) return;
      if (detail.autorId === userId) return; // não toca pra si mesmo

      const ehMinhaTabAtiva = !document.hidden;
      const conversaAbertaUrl = window.location.pathname.startsWith(`/chat/${detail.conversaId}`);

      // Som: toca sempre que receber, exceto quando estou olhando a própria conversa
      if (getSomAtivo() && !(ehMinhaTabAtiva && conversaAbertaUrl)) {
        tocarSom(getSomTipo(), getSomVolume());
      }

      // In-page Notification: só quando a aba não está em foco
      if (getInPageNotifAtivo() && !ehMinhaTabAtiva && "Notification" in window && Notification.permission === "granted") {
        // pega dados da conversa do cache
        const conversas = qc.getQueryData<any[]>(["chat-conversas"]) || [];
        const c = conversas.find((x) => x.id === detail.conversaId);
        const titulo = c ? (c.tipo === "ia" ? "IA" : c.nome) || "Nova mensagem" : "Nova mensagem";
        try {
          new Notification("RECDC OS — " + titulo, { body: detail.autorIa ? "IA respondeu" : "Nova mensagem", tag: `chat-${detail.conversaId}` });
        } catch {}
      }
    }

    window.addEventListener("growsoft:chat-mensagem", onMensagemEvent);
    return () => window.removeEventListener("growsoft:chat-mensagem", onMensagemEvent);
  }, [userId, qc]);
}
